import { useState, useEffect, useCallback, useRef } from "react";
import { RotateCcw, Loader2, AlertTriangle, CheckCircle, RefreshCw, AlertCircle, CreditCard, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const POLLING_INTERVAL = 3000;

export default function RefundPage() {
  const [paymentSessionId, setPaymentSessionId] = useState("");
  const [refundAmount, setRefundAmount] = useState("");
  const [refundPlatformFee, setRefundPlatformFee] = useState(true);
  const [refundType, setRefundType] = useState("auto");

  // Set page title
  useEffect(() => {
    document.title = "Refund | GeekTickets POS";
  }, []);
  const [loading, setLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [lastRefund, setLastRefund] = useState(null);
  const [polling, setPolling] = useState(false);
  const pollingRef = useRef(null);

  const handleAmountChange = (e) => {
    const value = e.target.value;
    if (/^\d*\.?\d{0,2}$/.test(value) || value === "") {
      setRefundAmount(value);
    }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  const pollTransactionStatus = useCallback(async (transactionId) => {
    try {
      const response = await axios.post(`${API}/transactions/${transactionId}/poll`);
      const { status, updated } = response.data;
      
      if (updated || status === "refunded" || status === "completed" || status === "failed") {
        setLastRefund(prev => ({ ...prev, status }));
        
        if (status === "refunded" || status === "completed") {
          toast.success("Refund processed successfully!");
          setPolling(false);
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
        } else if (status === "failed") {
          toast.error("Refund failed");
          setPolling(false);
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
        }
      }
    } catch (error) {
      console.error("Polling error:", error);
    }
  }, []);

  const startPolling = useCallback((transactionId) => {
    setPolling(true);
    pollingRef.current = setInterval(() => {
      pollTransactionStatus(transactionId);
    }, POLLING_INTERVAL);
    
    // Stop polling after 5 minutes
    setTimeout(() => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
        setPolling(false);
      }
    }, 5 * 60 * 1000);
  }, [pollTransactionStatus]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!paymentSessionId.trim()) {
      toast.error("Please enter a Payment Session ID");
      return;
    }

    // Validate session ID format
    if (!paymentSessionId.startsWith("ps_")) {
      toast.error("Invalid Session ID", {
        description: "Payment Session ID should start with 'ps_'"
      });
      return;
    }

    setShowConfirmDialog(true);
  };

  const processRefund = async () => {
    setShowConfirmDialog(false);
    setLoading(true);

    try {
      const payload = {
        payment_session_id: paymentSessionId.trim(),
        refund_platform_fee: refundPlatformFee,
        refund_type: refundType
      };

      if (refundAmount && parseFloat(refundAmount) > 0) {
        payload.amount = parseFloat(refundAmount);
      }

      const response = await axios.post(`${API}/refunds`, payload);

      setLastRefund(response.data);
      
      const isOnlineRefund = response.data.refund_type === "online";
      
      if (isOnlineRefund) {
        toast.success("Refund processed", {
          description: "Funds will be returned to the customer's account"
        });
      } else {
        toast.success("Refund initiated", {
          description: "Waiting for customer to present card on terminal"
        });
        // Start polling for in-person refunds only
        if (response.data.transaction_id) {
          startPolling(response.data.transaction_id);
        }
      }

      // Reset form
      setPaymentSessionId("");
      setRefundAmount("");

    } catch (error) {
      console.error("Refund error:", error);
      const errorMessage = error.response?.data?.detail || "Failed to initiate refund";
      toast.error("Refund failed", { description: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen p-4 sm:p-6 md:p-8 lg:p-12">
      <div className="max-w-lg mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-1">
          <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight">
            Process Refund
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            Enter payment session ID to refund
          </p>
        </div>

        {/* Warning Alert */}
        {refundType === "in_person" && (
          <Alert variant="destructive" className="border-red-200 bg-red-50">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle className="text-red-800 text-sm sm:text-base">Important</AlertTitle>
            <AlertDescription className="text-red-700 text-xs sm:text-sm">
              Customer must present the same card used for the original payment.
            </AlertDescription>
          </Alert>
        )}
        
        {refundType === "online" && (
          <Alert className="border-blue-200 bg-blue-50">
            <Globe className="h-4 w-4 text-blue-600" />
            <AlertTitle className="text-blue-800 text-sm sm:text-base">Online Refund</AlertTitle>
            <AlertDescription className="text-blue-700 text-xs sm:text-sm">
              Refund will be processed directly. No card presentation required.
            </AlertDescription>
          </Alert>
        )}

        {/* Refund Form */}
        <Card className="border-2 border-destructive/20">
          <CardHeader className="text-center pb-3 sm:pb-4 px-4 sm:px-6">
            <CardTitle className="flex items-center justify-center gap-2 text-base sm:text-lg">
              <RotateCcw className="w-4 h-4 sm:w-5 sm:h-5" />
              Refund Details
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              Enter the original payment session ID
            </CardDescription>
          </CardHeader>
          <CardContent className="px-4 sm:px-6">
            <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
              {/* Refund Type Selection */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">
                  Refund Type
                </Label>
                <RadioGroup
                  value={refundType}
                  onValueChange={setRefundType}
                  className="grid grid-cols-1 gap-2"
                  disabled={loading}
                >
                  <div className={`flex items-center space-x-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                    refundType === "auto" ? "border-primary bg-primary/5" : "border-muted hover:border-muted-foreground/30"
                  }`}>
                    <RadioGroupItem value="auto" id="auto" data-testid="refund-type-auto" />
                    <Label htmlFor="auto" className="flex-1 cursor-pointer">
                      <span className="font-medium">Auto-detect</span>
                      <p className="text-xs text-muted-foreground">Automatically choose based on transaction type</p>
                    </Label>
                  </div>
                  <div className={`flex items-center space-x-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                    refundType === "in_person" ? "border-primary bg-primary/5" : "border-muted hover:border-muted-foreground/30"
                  }`}>
                    <RadioGroupItem value="in_person" id="in_person" data-testid="refund-type-in-person" />
                    <Label htmlFor="in_person" className="flex-1 cursor-pointer">
                      <span className="font-medium flex items-center gap-2">
                        <CreditCard className="w-4 h-4" />
                        In-Person (Terminal)
                      </span>
                      <p className="text-xs text-muted-foreground">Customer presents card at terminal</p>
                    </Label>
                  </div>
                  <div className={`flex items-center space-x-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                    refundType === "online" ? "border-primary bg-primary/5" : "border-muted hover:border-muted-foreground/30"
                  }`}>
                    <RadioGroupItem value="online" id="online" data-testid="refund-type-online" />
                    <Label htmlFor="online" className="flex-1 cursor-pointer">
                      <span className="font-medium flex items-center gap-2">
                        <Globe className="w-4 h-4" />
                        Online / MOTO
                      </span>
                      <p className="text-xs text-muted-foreground">Direct refund without card present</p>
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Payment Session ID */}
              <div className="space-y-2">
                <Label htmlFor="sessionId" className="text-sm font-medium">
                  Payment Session ID <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="sessionId"
                  type="text"
                  placeholder="ps_XXXXXXXXXXXXXXXX"
                  value={paymentSessionId}
                  onChange={(e) => setPaymentSessionId(e.target.value)}
                  disabled={loading}
                  data-testid="session-id-input"
                  className="h-12 sm:h-14 font-mono text-xs sm:text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  Session ID from original payment (starts with "ps_")
                </p>
              </div>

              {/* Partial Refund Amount (Optional) */}
              <div className="space-y-2">
                <Label htmlFor="refundAmount" className="text-sm font-medium">
                  Refund Amount (Optional)
                </Label>
                <div className="relative">
                  <span className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-base sm:text-lg text-muted-foreground font-mono">
                    €
                  </span>
                  <Input
                    id="refundAmount"
                    type="text"
                    inputMode="decimal"
                    placeholder="Full refund"
                    value={refundAmount}
                    onChange={handleAmountChange}
                    disabled={loading}
                    data-testid="refund-amount-input"
                    className="h-12 sm:h-14 pl-9 sm:pl-10 font-mono text-sm sm:text-base"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Leave empty for full refund
                </p>
              </div>

              {/* Platform Fee Refund Option */}
              <div className="flex items-start space-x-3 p-3 sm:p-4 bg-muted/50 rounded-lg">
                <Checkbox
                  id="refundPlatformFee"
                  checked={refundPlatformFee}
                  onCheckedChange={setRefundPlatformFee}
                  disabled={loading}
                  data-testid="refund-fee-checkbox"
                  className="mt-0.5"
                />
                <div className="space-y-0.5">
                  <Label 
                    htmlFor="refundPlatformFee" 
                    className="text-sm font-medium cursor-pointer"
                  >
                    Refund Platform Fee
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Also refund the platform fee
                  </p>
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                variant="destructive"
                size="lg"
                disabled={loading || !paymentSessionId.trim()}
                data-testid="refund-button"
                className="w-full h-12 sm:h-14 text-base sm:text-lg font-semibold"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <RotateCcw className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                    Initiate Refund
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Last Refund Info */}
        {lastRefund && (
          <Card className={`border ${
            lastRefund.status === "refunded" || lastRefund.status === "completed"
              ? "border-green-200 bg-green-50/50" 
              : lastRefund.status === "failed"
              ? "border-red-200 bg-red-50/50"
              : "border-yellow-200 bg-yellow-50/50"
          }`}>
            <CardContent className="pt-4 sm:pt-6 px-4 sm:px-6">
              <div className="flex items-start gap-3">
                {lastRefund.status === "refunded" || lastRefund.status === "completed" ? (
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                ) : lastRefund.status === "failed" ? (
                  <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                ) : (
                  <RefreshCw className={`w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0 ${polling ? "animate-spin" : ""}`} />
                )}
                <div className="flex-1 space-y-2 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className={`font-medium text-sm sm:text-base ${
                      lastRefund.status === "refunded" || lastRefund.status === "completed"
                        ? "text-green-800" 
                        : lastRefund.status === "failed"
                        ? "text-red-800"
                        : "text-yellow-800"
                    }`}>
                      {lastRefund.status === "refunded" || lastRefund.status === "completed"
                        ? "Refund Completed" 
                        : lastRefund.status === "failed"
                        ? "Refund Failed"
                        : "Refund In Progress"}
                    </p>
                    {polling && (
                      <span className="text-xs text-yellow-600 flex items-center gap-1">
                        <RefreshCw className="w-3 h-3 animate-spin" />
                        Polling
                      </span>
                    )}
                  </div>
                  <div className={`text-xs sm:text-sm space-y-1 ${
                    lastRefund.status === "refunded" || lastRefund.status === "completed"
                      ? "text-green-700" 
                      : lastRefund.status === "failed"
                      ? "text-red-700"
                      : "text-yellow-700"
                  }`}>
                    <p>Status: <span className="font-medium capitalize">{lastRefund.status?.replace("_", " ")}</span></p>
                    <p className="break-all">
                      Session: <span className="font-mono text-xs">{lastRefund.payment_session_id}</span>
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Confirmation Dialog */}
        <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
          <AlertDialogContent className="max-w-[90vw] sm:max-w-md mx-4">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-base sm:text-lg">
                <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-destructive" />
                Confirm Refund
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-3 text-sm text-muted-foreground">
                  <span className="block">You are about to initiate a refund for:</span>
                  <div className="bg-muted p-2 sm:p-3 rounded-lg font-mono text-xs break-all text-foreground">
                    {paymentSessionId}
                  </div>
                  {refundAmount && (
                    <span className="block">
                      Refund amount: <span className="font-mono font-medium">€{parseFloat(refundAmount).toFixed(2)}</span>
                    </span>
                  )}
                  {!refundAmount && (
                    <span className="block font-medium">This will process a full refund.</span>
                  )}
                  <span className="block text-destructive font-medium">
                    This action cannot be undone.
                  </span>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex-col sm:flex-row gap-2">
              <AlertDialogCancel data-testid="cancel-refund-button" className="w-full sm:w-auto">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={processRefund}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90 w-full sm:w-auto"
                data-testid="confirm-refund-button"
              >
                Confirm Refund
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
